import React, { ReactNode, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useModal } from 'hooks/useModal';
import { Add } from '@mui/icons-material';
import { Box, Button } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { ModalsKeys, SelectOption } from 'global';
import { FormInputSelect } from 'components/form';
import { useDispatch, useSelector } from 'react-redux';
import { accountSelectors } from 'redux/accountReducer';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { ProposalProduct } from '@trnsact/trnsact-shared-types/dist/generated';

import { TEST_IDS } from 'shared';
import { setMenuDeps } from '../../../lib';
import { useSaleMode } from '../../../context';
import { FinanceProgramFormValues } from '../../../types';
import { GET_PROPOSAL_MENUS_SELECT_OPTIONS } from '../../../api';
import { deskingActions, deskingSelectors } from '../../../model';
import { MenuSelectsResponse, MenuSelectsVariables } from '../../../api/types';

interface Props {
  withAddProductsFeature?: boolean;
  createProductSlot?: ReactNode;
}

export const MenuSelector = ({ withAddProductsFeature = true, createProductSlot }: Props) => {
  const classes = useStyles();

  const [menuSelectsOptions, setMenuSelectsOptions] = useState<SelectOption[]>([]);

  const { isCash } = useSaleMode();

  const { handleOpen: onOpenProposalProductsDialog } = useModal(ModalsKeys.ProposalProductsDialog);
  const { handleOpen } = useModal(ModalsKeys.DeskingNotifyAboutRemovedProductsDialog);

  const { control, setValue, getValues, resetField } = useFormContext<FinanceProgramFormValues>();
  const menu = useWatch({ control, name: 'financeQuote.menu' });

  const dispatch = useDispatch();

  const accountId = useSelector(accountSelectors.accountId);
  const proposalMenus = useSelector(deskingSelectors.proposalMenus);
  const products = useSelector(deskingSelectors.proposalsProducts);
  const isProposalsProductsLoading = useSelector<any, boolean>(deskingSelectors.isProposalsProductsLoading);
  const selectedProducts = useSelector(deskingSelectors.selectedProposalsProductsArray);
  const selectedProposalsProductsIds = useSelector(deskingSelectors.selectedProposalsProductsIds);
  const proposalsProductsIds = useSelector<any, string[]>(deskingSelectors.proposalsProductsIds);

  useQuery<MenuSelectsResponse, MenuSelectsVariables>(GET_PROPOSAL_MENUS_SELECT_OPTIONS, {
    skip: !accountId,
    variables: { accountId },
    onCompleted(data) {
      const optionsWithDateAndTime = data.proposalMenus;

      const options = optionsWithDateAndTime.map(({ modifiedDateTime, ...option }) => option);

      setMenuSelectsOptions(options);
    },
  });

  const handleChangeMenu = (nextValue: string) => {
    resetField('financeProgramDescription');
    setValue('financeQuote.menu', nextValue);
    resetField('displaySettings');

    setMenuDeps({
      isCash,
      dispatch,
      products,
      handleOpen,
      proposalMenus,
      proposalsProductsIds,
      proposalMenuId: nextValue,
      terms: getValues('financeQuote.terms').map(Number),
    });
  };

  const handleProductsDialogConfirm = (selectedProducts: ProposalProduct[]) => {
    dispatch(deskingActions.updateProducts({ products: selectedProducts }));
  };

  return (
    <Box className={classes.container}>
      <Controller
        control={control}
        name="financeQuote.menu"
        render={({ field }) => (
          <FormInputSelect
            variant="standard"
            label="Select Menu Template"
            options={menuSelectsOptions}
            isLoading={isProposalsProductsLoading}
            dataTestId={TEST_IDS.desking.menu.selectTemplate}
            disabled={isProposalsProductsLoading || !menuSelectsOptions.length}
            {...field}
            onChange={handleChangeMenu}
          />
        )}
      />

      <Box className="actions">
        {withAddProductsFeature && menu && (
          <Button
            startIcon={<Add />}
            disabled={isProposalsProductsLoading}
            data-testid={TEST_IDS.desking.menu.actions.addProduct}
            onClick={() => {
              onOpenProposalProductsDialog({
                products,
                selectedProposalsProductsIds,
                onConfirm: handleProductsDialogConfirm,
              });
            }}
          >
            {!!selectedProducts?.length ? 'Add more' : 'Add product'}
          </Button>
        )}

        {menu && createProductSlot}
      </Box>
    </Box>
  );
};

const useStyles = makeStyles(({ breakpoints }) => ({
  container: {
    gap: '0.5rem',
    display: 'grid',
    alignItems: 'baseline',
    gridTemplateColumns: '1fr auto',
    [breakpoints.down('lg')]: {
      gridTemplateColumns: '1fr',
    },

    '& .actions': {
      gap: '0.5rem',
      display: 'grid',
      gridTemplateColumns: 'auto auto',
    },
  },
}));
